import {
    ComponentDescription,
    PropertyDescription,
    PropertyKind,
    TableGlideType,
    MutatingScreenKind,
    InputOutputTables,
} from "../description";
import { ComponentHandlerBase } from "./handler";
import { ImageAspectRatio } from "./image-types";
import { ComponentBuilder, ComponentDescriptor, ComponentConfiguratorContext } from ".";
import { SerializedComponent } from "../../components/SerializedApp";
import { PropertySection } from "./property-sections";
import { getEnumProperty, getSwitchProperty } from "./property-utils";
import { getFeatureSetting } from "../../feature-settings";
import { getDocURL } from "../../docUrl";
import { linkPropertyDescriptor } from "./link";
import { makeText } from "../builder-utils";

const ComponentKindWebview = "webview";

interface WebviewDescription extends ComponentDescription {
    readonly propertyName: PropertyDescription;
    readonly aspectRatio: PropertyDescription;
    readonly allowScrolling: PropertyDescription;
}

export class WebviewComponentHandler extends ComponentHandlerBase<WebviewDescription> {
    constructor() {
        super(ComponentKindWebview);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: WebviewDescription,
        { input }: InputOutputTables
    ): SerializedComponent {
        const { b } = builder;

        const link = makeText(builder, desc.propertyName, input, true, false, true);
        if (link === undefined) return b.emptyUI;

        return b.let(
            [
                ["extractedFrame", b.extractIframe(link)],
                ["link", b.getNonTableProperty("source", b.getVariable("extractedFrame"))],
                ["data", b.classifyLink(b.getVariable("link"))],
            ],
            b.ui("Webview", {
                link: b.getVariable("link"),
                aspectRatio: b.constant(getEnumProperty(desc.aspectRatio)),
                title: b.getNonTableProperty("title", b.getVariable("data")),
                allowScrolling: b.and(
                    b.getNonTableProperty("scrollable", b.getVariable("extractedFrame")),
                    b.constant(getSwitchProperty(desc.allowScrolling))
                ),
                explicitHeight: b.getNonTableProperty("height", b.getVariable("extractedFrame")),
            })
        );
    }

    public getDescriptor(
        _desc: WebviewDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<WebviewDescription> {
        return {
            name: "Webview",
            description: "A view that embeds other web assets",
            img: "componentWebView",
            group: "Media",
            helpUrl: getDocURL("webview"),
            isLegacy: !getFeatureSetting("webviewComponent"),
            isPro: true,
            properties: [
                linkPropertyDescriptor("propertyName", mutatingScreenKind, false),
                {
                    kind: PropertyKind.Switch,
                    property: { name: "allowScrolling" },
                    label: "Allow scrolling",
                    defaultValue: true,
                    section: PropertySection.Design,
                },
                {
                    kind: PropertyKind.Enum,
                    property: { name: "aspectRatio" },
                    label: "Size",
                    menuLabel: "Size",
                    cases: [
                        {
                            value: ImageAspectRatio.ThreeByOne,
                            label: "Small",
                            icon: "border3by1",
                        },
                        {
                            value: ImageAspectRatio.ThreeByTwo,
                            label: "Medium",
                            icon: "border3by2",
                        },
                        {
                            value: ImageAspectRatio.Square,
                            label: "Square",
                            icon: "borderSquare",
                        },
                        {
                            value: ImageAspectRatio.ThreeByFour,
                            label: "Large",
                            icon: "border3by4",
                        },
                        { value: ImageAspectRatio.Fit, label: "Filling", icon: "borderOriginal" },
                        { value: ImageAspectRatio.Expand, label: "Expand", icon: "borderOriginal" },
                    ],
                    defaultCaseValue: ImageAspectRatio.Fit,
                    section: PropertySection.Design,
                    visual: "small-images",
                },
                ...this.getBasePropertyDescriptors(),
            ],
        };
    }
}
